import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormArray, FormBuilder, FormGroup} from "@angular/forms";
import {CommissionService} from "../../service/commission.service";
import {AssignedCard} from "../../interface/assigned-card.interface";
import {CommissionTemplate} from "../../interface/commission-template.interface";
import {PaymentMethodConfig} from "../../models/payment-method-config.model";
import {PaymentMethodConfigService} from "../../service/payment-method-config.service";
import {MatDialog, MatDialogConfig} from "@angular/material/dialog";
import {CommissionTabDialogComponent} from "./commission-tab-dialog/commission-tab-dialog.component";
import {forkJoin, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-commissions',
  templateUrl: './commissions.component.html',
  styleUrl: './commissions.component.scss'
})
export class CommissionsComponent implements OnInit, OnDestroy {
  templates: CommissionTemplate[] = [];
  activeTemplate: CommissionTemplate | null = null;
  activeTemplateIndex: number = 0;
  form!: FormGroup;
  paymentMethods: PaymentMethodConfig[] = [];
  assignedCards: AssignedCard[] = [];
  allCards: AssignedCard[] = [];
  availableCards: AssignedCard[] = [];
  availableCardIds = new Set<number>();
  excludedCardIds = new Set<number>();
  selectedCardIds: number[] = [];
  loading: boolean = false;
  saving: boolean = false;
  editMode: boolean = false;
  cardSearchQuery: string = '';
  @ViewChild('tabsContainer') tabsContainer!: ElementRef;

  private formSubs$ = new Subject<void>();

  constructor(private readonly commissionService: CommissionService,
              private readonly paymentMethodConfigService: PaymentMethodConfigService,
              private readonly fb: FormBuilder,
              private readonly dialog: MatDialog) {}

  ngOnInit(): void {
    this.paymentMethodConfigService.getAll().subscribe({
      next: (methods) => {
        this.paymentMethods = this.sortPaymentMethodsForFigma(methods);
        this.loadTemplates();
      }
    });
  }

  ngOnDestroy(): void {
    this.formSubs$.next();
    this.formSubs$.complete();
  }

  private sortPaymentMethodsForFigma(methods: PaymentMethodConfig[]): PaymentMethodConfig[] {
    const figmaOrder: string[] = [
      'APPLE_PAY',
      'GOOGLE_PAY',
      'IDRAM',
      'TELCELL',
      'EASY_WALLET',
      'FASTSHIFT',
      'INECO_PAY',
      'MY_PAY',
      'BANK_OF_GEORGIA',
      'YO_MONEY',
      'SBER_BANK',
      'T_BANK',
      'CREDIT_CARD'
    ];

    const methodMap = new Map<string, PaymentMethodConfig>();
    methods.forEach(m => methodMap.set(m.code, m));

    const ordered: PaymentMethodConfig[] = [];
    figmaOrder.forEach(code => {
      const method = methodMap.get(code);
      if (method) {
        ordered.push(method);
        methodMap.delete(code);
      }
    });
    methodMap.forEach(method => ordered.push(method));
    return ordered;
  }

  private toDisplayUnit(backendUnit: string | null | undefined): string {
    if (backendUnit === 'MONEY' || backendUnit === 'AMD') return 'AMD';
    return '%';
  }

  private toBackendUnit(displayUnit: string | null | undefined): string {
    return displayUnit === 'AMD' ? 'MONEY' : 'PERCENT';
  }

  private preserveTemplateOrder(newTemplates: CommissionTemplate[]): CommissionTemplate[] {
    if (this.templates.length > 0) {
      const existingOrder = this.templates.map(t => t.id);
      const templateMap = new Map(newTemplates.map(t => [t.id, t]));
      const ordered: CommissionTemplate[] = [];
      existingOrder.forEach(id => {
        const template = templateMap.get(id);
        if (template) {
          ordered.push(template);
          templateMap.delete(id);
        }
      });
      templateMap.forEach(template => ordered.push(template));
      return ordered;
    }
    return newTemplates;
  }

  loadTemplates(selectLast: boolean = false): void {
    this.loading = true;
    const currentActiveId = this.activeTemplate?.id;
    this.commissionService.getAllTemplates().subscribe({
      next: (templates) => {
        const orderedTemplates = this.preserveTemplateOrder(templates);
        this.templates = orderedTemplates;
        if (templates.length > 0) {
          if (selectLast) {
            this.activeTemplateIndex = orderedTemplates.length - 1;
          } else if (currentActiveId) {
            const newIndex = orderedTemplates.findIndex(t => t.id === currentActiveId);
            this.activeTemplateIndex = newIndex >= 0 ? newIndex : 0;
          } else {
            this.activeTemplateIndex = 0;
          }
          this.selectTemplate(this.activeTemplateIndex);
        }
        this.loading = false;
      }, error: () => {
        this.loading = false;
      }
    });
  }

  private refreshTemplateInPlace(templateId: number): void {
    this.commissionService.getTemplateById(templateId).subscribe({
      next: (updated) => {
        const idx = this.templates.findIndex(t => t.id === templateId);
        if (idx >= 0) {
          this.templates[idx] = updated;
          if (this.activeTemplateIndex === idx) {
            this.activeTemplate = updated;
            this.buildForm();
          }
        }
      }
    });
  }

  selectTemplate(index: number): void {
    this.activeTemplateIndex = index;
    this.activeTemplate = this.templates[index];
    this.cardSearchQuery = '';
    this.editMode = false;
    this.buildForm();
    this.loadCards();
  }

  enableEditMode(): void {
    this.editMode = true;
    this.applyEditState();
  }

  private applyEditState(): void {
    if (!this.form) return;
    if (this.editMode) {
      this.form.enable({emitEvent: false});
    } else {
      this.form.disable({emitEvent: false});
    }
  }

  buildForm(): void {
    if (!this.activeTemplate) return;
    // Cancel subscriptions from previous form instance
    this.formSubs$.next();

    const t = this.activeTemplate;
    this.form = this.fb.group({
      name: [t.name],
      applyToAllStandard: [t.applyToAllStandard],
      applyToAllExtra: [t.applyToAllExtra],
      globalStandardValue: [t.globalStandardValue || 0],
      globalStandardUnit: [this.toDisplayUnit(t.globalStandardUnit)],
      globalExtraValue: [t.globalExtraValue || 0],
      globalExtraUnit: [this.toDisplayUnit(t.globalExtraUnit)],
      methods: this.fb.array(this.paymentMethods.map(pm => {
        const key = pm.code;
        const existing = t.methods?.find(m => m.paymentMethod === key);
        return this.fb.group({
          paymentMethod: [key],
          standardValue: [existing?.standardValue || 0],
          standardUnit: [this.toDisplayUnit(existing?.standardUnit)],
          extraValue: [existing?.extraValue || 0],
          extraUnit: [this.toDisplayUnit(existing?.extraUnit)]
        });
      }))
    });

    this.setupFormSubscriptions();

    // If checkboxes are already checked on load, sync global input from row values
    if (this.applyToAllStandard) this.syncStandard(false);
    if (this.applyToAllExtra) this.syncExtra(false);

    this.applyEditState();
  }

  private setupFormSubscriptions(): void {
    // Global unit validity check → auto-uncheck; if valid and checked → push to rows
    this.form.get('globalStandardUnit')!.valueChanges
      .pipe(takeUntil(this.formSubs$))
      .subscribe(val => {
        if (val !== '%' && val !== 'AMD') {
          this.form.get('applyToAllStandard')?.setValue(false, {emitEvent: false});
        } else if (this.applyToAllStandard) {
          this.syncStandard(true);
        }
      });

    this.form.get('globalExtraUnit')!.valueChanges
      .pipe(takeUntil(this.formSubs$))
      .subscribe(val => {
        if (val !== '%' && val !== 'AMD') {
          this.form.get('applyToAllExtra')?.setValue(false, {emitEvent: false});
        } else if (this.applyToAllExtra) {
          this.syncExtra(true);
        }
      });

    // Checkbox toggled: promote common row value → global first, then push global → rows
    this.form.get('applyToAllStandard')!.valueChanges
      .pipe(takeUntil(this.formSubs$))
      .subscribe(() => this.syncStandard(false));

    this.form.get('applyToAllExtra')!.valueChanges
      .pipe(takeUntil(this.formSubs$))
      .subscribe(() => this.syncExtra(false));

    // Global value changed by user → push to all rows
    this.form.get('globalStandardValue')!.valueChanges
      .pipe(takeUntil(this.formSubs$))
      .subscribe(() => {
        if (this.applyToAllStandard) this.syncStandard(true);
      });

    this.form.get('globalExtraValue')!.valueChanges
      .pipe(takeUntil(this.formSubs$))
      .subscribe(() => {
        if (this.applyToAllExtra) this.syncExtra(true);
      });

    // Individual row edited by user → update global if all rows agree, else reset global
    this.methodsArray.valueChanges
      .pipe(takeUntil(this.formSubs$))
      .subscribe(() => {
        const controls = this.methodsArray.controls;
        if (this.applyToAllStandard) {
          const firstVal = controls[0]?.get('standardValue')?.value;
          const firstUnit = controls[0]?.get('standardUnit')?.value;
          const allEqual = controls.every(c =>
            c.get('standardValue')?.value === firstVal &&
            c.get('standardUnit')?.value === firstUnit
          );
          if (allEqual && firstVal !== undefined) {
            this.form.get('globalStandardValue')?.setValue(firstVal, {emitEvent: false});
            this.form.get('globalStandardUnit')?.setValue(firstUnit, {emitEvent: false});
          } else {
            this.form.get('globalStandardValue')?.setValue(0, {emitEvent: false});
          }
        }
        if (this.applyToAllExtra) {
          const firstVal = controls[0]?.get('extraValue')?.value;
          const firstUnit = controls[0]?.get('extraUnit')?.value;
          const allEqual = controls.every(c =>
            c.get('extraValue')?.value === firstVal &&
            c.get('extraUnit')?.value === firstUnit
          );
          if (allEqual && firstVal !== undefined) {
            this.form.get('globalExtraValue')?.setValue(firstVal, {emitEvent: false});
            this.form.get('globalExtraUnit')?.setValue(firstUnit, {emitEvent: false});
          } else {
            this.form.get('globalExtraValue')?.setValue(0, {emitEvent: false});
          }
        }
      });
  }

  // fromGlobal=true: global changed, push down to rows
  // fromGlobal=false: checkbox just toggled, read rows first if enabling, then sync
  private syncStandard(fromGlobal: boolean): void {
    const controls = this.methodsArray.controls;

    if (!this.applyToAllStandard) return;

    if (!fromGlobal && controls.length > 0) {
      const firstVal = controls[0].get('standardValue')?.value;
      const firstUnit = controls[0].get('standardUnit')?.value;
      const allEqual = controls.every(c =>
        c.get('standardValue')?.value === firstVal &&
        c.get('standardUnit')?.value === firstUnit
      );
      if (allEqual && firstVal !== undefined) {
        this.form.get('globalStandardValue')?.setValue(firstVal, {emitEvent: false});
        this.form.get('globalStandardUnit')?.setValue(firstUnit, {emitEvent: false});
      }
    }

    const val = this.form.get('globalStandardValue')?.value ?? 0;
    const unit = this.form.get('globalStandardUnit')?.value ?? '%';
    controls.forEach(ctrl => {
      ctrl.get('standardValue')?.setValue(val, {emitEvent: false});
      ctrl.get('standardUnit')?.setValue(unit, {emitEvent: false});
    });
  }

  private syncExtra(fromGlobal: boolean): void {
    const controls = this.methodsArray.controls;

    if (!this.applyToAllExtra) return;

    if (!fromGlobal && controls.length > 0) {
      const firstVal = controls[0].get('extraValue')?.value;
      const firstUnit = controls[0].get('extraUnit')?.value;
      const allEqual = controls.every(c =>
        c.get('extraValue')?.value === firstVal &&
        c.get('extraUnit')?.value === firstUnit
      );
      if (allEqual && firstVal !== undefined) {
        this.form.get('globalExtraValue')?.setValue(firstVal, {emitEvent: false});
        this.form.get('globalExtraUnit')?.setValue(firstUnit, {emitEvent: false});
      }
    }

    const val = this.form.get('globalExtraValue')?.value ?? 0;
    const unit = this.form.get('globalExtraUnit')?.value ?? '%';
    controls.forEach(ctrl => {
      ctrl.get('extraValue')?.setValue(val, {emitEvent: false});
      ctrl.get('extraUnit')?.setValue(unit, {emitEvent: false});
    });
  }

  get methodsArray(): FormArray {
    return this.form?.get('methods') as FormArray;
  }

  get applyToAllStandard(): boolean {
    return this.form?.get('applyToAllStandard')?.value;
  }

  get applyToAllExtra(): boolean {
    return this.form?.get('applyToAllExtra')?.value;
  }

  get methodRowColumns(): string {
    return '1fr 140px 140px';
  }

  getMaxValue(unit: string | null | undefined): number {
    return unit === '%' ? 99 : 999999999;
  }

  loadCards(): void {
    if (!this.activeTemplate) return;
    this.commissionService.getAssignedCards(this.activeTemplate.id).subscribe({
      next: (cards) => {
        this.assignedCards = cards;
        this.selectedCardIds = cards.map(c => c.userCardId);
      }
    });
    this.loadExcludedCards();
    this.commissionService.getAllCards().subscribe({
      next: (cards) => {
        this.allCards = cards;
      }
    });
    this.commissionService.getAvailableCards().subscribe({
      next: (cards) => {
        this.availableCards = cards;
        this.availableCardIds = new Set(cards.map(card => card.userCardId));
      }
    });
  }

  private loadExcludedCards(): void {
    this.excludedCardIds = new Set<number>();
    const lockedTemplateIds = this.templates
      .filter(t => this.isFeeOrMenuTemplate(t.name))
      .map(t => t.id);
    if (lockedTemplateIds.length === 0) return;
    forkJoin(lockedTemplateIds.map(id => this.commissionService.getAssignedCards(id))).subscribe({
      next: (results) => {
        this.excludedCardIds = new Set(results.flat().map(c => c.userCardId));
      }
    });
  }

  private isFeeOrMenuTemplate(name: string | null | undefined): boolean {
    const normalized = (name || '').toLowerCase();
    return normalized === 'fee' || normalized === 'menu';
  }

  getMethodInfo(index: number): PaymentMethodConfig {
    return this.paymentMethods[index];
  }

  save(): void {
    if (!this.activeTemplate || !this.form) return;
    if (!this.isFormValidByBusinessRules()) return;
    this.saving = true;
    const formValue = this.form.getRawValue();
    const request = {
      name: this.activeTemplate.name,
      applyToAllStandard: formValue.applyToAllStandard,
      applyToAllExtra: formValue.applyToAllExtra,
      globalStandardValue: formValue.globalStandardValue,
      globalStandardUnit: this.toBackendUnit(formValue.globalStandardUnit),
      globalExtraValue: formValue.globalExtraValue,
      globalExtraUnit: this.toBackendUnit(formValue.globalExtraUnit),
      methods: formValue.methods.map((m: any) => ({
        ...m,
        standardUnit: this.toBackendUnit(m.standardUnit),
        extraUnit: this.toBackendUnit(m.extraUnit)
      }))
    };
    const currentIndex = this.activeTemplateIndex;
    this.commissionService.updateTemplate(this.activeTemplate.id, request).subscribe({
      next: (updated) => {
        this.templates[currentIndex] = updated;
        this.activeTemplate = updated;
        this.editMode = false;
        this.buildForm();
        this.saving = false;
      }, error: () => {
        this.saving = false;
      }
    });
  }

  cancel(): void {
    this.editMode = false;
    this.buildForm();
    this.loadCards();
  }

  openCreateDialog(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    const dialogRef = this.dialog.open(CommissionTabDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const request = {
          ...result,
          globalStandardUnit: this.toBackendUnit(result.globalStandardUnit),
          globalExtraUnit: this.toBackendUnit(result.globalExtraUnit)
        };
        this.commissionService.createTemplate(request).subscribe({
          next: (newTemplate) => {
            this.templates.push(newTemplate);
            this.activeTemplateIndex = this.templates.length - 1;
            this.selectTemplate(this.activeTemplateIndex);
          }
        });
      }
    });
  }

  editTemplateName(): void {
    if (!this.activeTemplate) return;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {name: this.activeTemplate.name};
    const dialogRef = this.dialog.open(CommissionTabDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result && this.activeTemplate) {
        const currentIndex = this.activeTemplateIndex;
        const formValue = this.form.getRawValue();
        const request = {
          ...formValue,
          name: result.name,
          globalStandardUnit: this.toBackendUnit(formValue.globalStandardUnit),
          globalExtraUnit: this.toBackendUnit(formValue.globalExtraUnit),
          methods: formValue.methods.map((m: any) => ({
            ...m,
            standardUnit: this.toBackendUnit(m.standardUnit),
            extraUnit: this.toBackendUnit(m.extraUnit)
          }))
        };
        this.commissionService.updateTemplate(this.activeTemplate.id, request).subscribe({
          next: (updated) => {
            this.templates[currentIndex] = updated;
            this.activeTemplate = updated;
          }
        });
      }
    });
  }

  deleteTemplate(): void {
    if (!this.activeTemplate || this.activeTemplate.isFixed) return;
    this.commissionService.deleteTemplate(this.activeTemplate.id).subscribe({
      next: () => {
        this.activeTemplateIndex = 0;
        this.loadTemplates();
      }
    });
  }

  assignCards(): void {
    if (!this.activeTemplate) return;
    const cardIds = this.selectedCardIds.filter(id => id !== -1);
    this.commissionService.assignCards(this.activeTemplate.id, cardIds).subscribe({
      next: () => {
        this.refreshTemplateInPlace(this.activeTemplate!.id);
        this.loadCards();
      }
    });
  }

  toggleAllCards(): void {
    const allCards = this.getAllCardsForDropdown();
    const allIds = allCards.map(c => c.userCardId);
    if (this.selectedCardIds.length === allIds.length) {
      this.selectedCardIds = [];
    } else {
      this.selectedCardIds = [...allIds];
    }
  }

  scrollTabs(): void {
    const container = document.querySelector('.tabs-scroll');
    if (container) {
      container.scrollBy({left: 150, behavior: 'smooth'});
    }
  }

  getCardDisplay(card: AssignedCard): string {
    const parts = [card.name, card.surname].filter(p => p);
    const fullName = parts.length > 0 ? parts.join(' ') : '';
    return card.qrUsername ? `${card.qrUsername}${fullName ? ' - ' + fullName : ''}` : fullName || card.cardId;
  }

  getAllCardsForDropdown(): AssignedCard[] {
    let cards: AssignedCard[];
    if (this.isDefaultTemplate()) {
      cards = this.allCards;
    } else {
      const assignedMap = new Map(this.assignedCards.map(card => [card.userCardId, card]));
      const allMap = new Map(this.allCards.map(card => [card.userCardId, card]));
      cards = [...assignedMap.values(), ...allMap.values()]
        .filter((card, index, arr) => arr.findIndex(c => c.userCardId === card.userCardId) === index);
    }
    return cards.filter(card =>
      !this.excludedCardIds.has(card.userCardId) || this.selectedCardIds.includes(card.userCardId));
  }

  getFilteredCardsForDropdown(): AssignedCard[] {
    const cards = this.getAllCardsForDropdown();
    if (!this.cardSearchQuery.trim()) return cards;
    const query = this.cardSearchQuery.toLowerCase().trim();
    return cards.filter(card => (card.qrUsername || '').toLowerCase().includes(query));
  }

  onCardSearch(event: Event): void {
    this.cardSearchQuery = (event.target as HTMLInputElement).value;
  }

  onDropdownOpenChanged(isOpen: boolean): void {
    if (!isOpen) {
      this.cardSearchQuery = '';
    }
  }

  canDelete(): boolean {
    return !!this.activeTemplate && !this.activeTemplate.isFixed && (this.activeTemplate.assignedCardsCount ?? 0) === 0;
  }

  isCardAvailable(card: AssignedCard): boolean {
    if (this.isDefaultTemplate()) {
      return false;
    }
    return this.availableCardIds.has(card.userCardId);
  }

  private isDefaultTemplate(): boolean {
    return (this.activeTemplate?.name || '').toLowerCase() === 'default';
  }

  private isFormValidByBusinessRules(): boolean {
    const formValue = this.form.getRawValue();
    return this.validateValueByUnit(formValue.globalStandardValue, formValue.globalStandardUnit)
      && this.validateValueByUnit(formValue.globalExtraValue, formValue.globalExtraUnit)
      && formValue.methods.every((m: any) =>
        this.validateValueByUnit(m.standardValue, m.standardUnit)
        && this.validateValueByUnit(m.extraValue, m.extraUnit)
      );
  }

  private validateValueByUnit(value: number, unit: string): boolean {
    if (value == null || value < 0) return false;
    return unit !== '%' || value <= 99;
  }

  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    target.style.display = 'none';
  }
}
